// shop.ts — the MacBook listings grid. A flat, ungrouped view of every
// listing across every retailer, with condition flags on each card and
// left-rail spec filters. One fast request, client-side filter/sort,
// incremental render. Tapping a card goes straight to the seller.
//   /shop · /shop/macbook · /shop/laptops → the grid
//   /shop/<cat>/<slug>                    → single product view (deep links)
// Reads the product graph via /api/shop/*. No framework.

import { esc } from './ui.js';

const app = document.getElementById('app')!;

// ---- types (mirror src/lib/truth.ts FlatOffer) -----------------------------
interface FaultView { category: string; severity: string; title: string; detail: string; evidence_url: string | null; verified: boolean; }
interface FlatOffer {
  id: string; slug: string; name: string; brand: string; model: string; variant: string | null;
  image_url: string | null; msrp_usd: string | null;
  chip: string | null; ram_gb: number | null; storage: string | null; screen_in: number | null;
  retailer: string; title: string | null; condition: string; condition_grade: string | null;
  price_usd: string; list_price_usd: string | null; in_stock: boolean;
  seller: string | null; seller_rating: string | null;
  url: string; affiliate_url: string | null; crawl_source: string;
  faults: FaultView[];
}
// product-page types (deep links)
interface ClaimView { claim_text: string; claim_type: string; reality: string | null; delta: string; note: string | null; }
interface OfferView extends FlatOffer { warranty: string | null; returns: string | null; shipping_usd: string | null; claims: ClaimView[]; }
interface ProductView {
  id: string; slug: string; category: string; brand: string; model: string; variant: string | null; name: string;
  specs: Record<string, unknown> | null; summary: string | null; image_url: string | null; msrp_usd: string | null;
  offers: OfferView[]; best_price: string | null;
}

const RETAILER_LABEL: Record<string, string> = {
  apple: 'Apple', amazon: 'Amazon', walmart: 'Walmart', ebay: 'eBay',
  bhphoto: 'B&H Photo', backmarket: 'Back Market', bestbuy: 'Best Buy',
};
const COND_LABEL: Record<string, string> = { new: 'New', refurb: 'Refurbished', open_box: 'Open box', used: 'Used', renewed: 'Renewed' };
const COND_ORDER = ['new', 'open_box', 'refurb', 'renewed', 'used'];
const DELTA_LABEL: Record<string, string> = { match: 'Accurate', overstated: 'Overstated', misleading: 'Misleading', false: 'False', unverified: 'Unverified' };
const FAULT_LABEL: Record<string, string> = {
  warranty: 'No / unclear warranty', battery: 'Battery health unknown', authenticity: 'Verify authenticity',
  cosmetic: 'Cosmetic wear', missing_accessory: 'Missing accessory', fine_print: 'Fine print',
  performance: 'Performance issue', shipping: 'Shipping caveat', screen: 'Screen issue',
};

// macOS support estimate by chip family (Apple Silicon: ~7-8 major releases
// from launch). Honest estimate — labelled as such, never implied as official.
const MACOS_UNTIL: Record<string, string> = { M1: '2027', M2: '2029', M3: '2030', M4: '2032' };
// Collapse the messy chip strings retailers use — "Apple M4 Pro (12-core CPU,
// 16-core GPU)", "Apple M3 (8-core CPU, 10-core GPU)", "M3" — to one canonical
// short label (M3, M4, M4 Pro) so the filter groups cleanly and cards read
// simply. Core-count variants of the same chip collapse together.
function normalizeChip(chip: string | null): string | null {
  if (!chip) return null;
  const m = /\bM(\d)\b\s*(Pro|Max|Ultra)?/i.exec(chip);
  if (!m) return chip.trim();
  const tier = m[2] ? ` ${m[2][0]!.toUpperCase()}${m[2]!.slice(1).toLowerCase()}` : '';
  return `M${m[1]}${tier}`;
}
function chipFamily(chip: string | null): string | null {
  const n = normalizeChip(chip);
  const m = n ? /^M(\d)/.exec(n) : null;
  return m ? `M${m[1]}` : null;
}
function macosUntil(chip: string | null): string | null {
  const fam = chipFamily(chip);
  return fam ? (MACOS_UNTIL[fam] || null) : null;
}

// Official Apple stock photo per MacBook variant — clean white-background
// renders from Apple's "Identify your Mac" support pages (cdsassets.apple.com,
// one per model, stable URLs, verified image/*). Mirrors the crawler's
// appleStockImage() so the live grid matches what the truth graph stores.
// Falls back to the stored image_url, then the designed placeholder.
const A_CDN = 'https://cdsassets.apple.com/live/7WUAS350/images';
function macbookImage(model: string, chip: string | null, screen: number | null): string | null {
  const fam = chipFamily(chip);            // "M1".."M4" or null
  const g = fam ? Number(fam[1]) : 0;      // chip generation 1..4 (0 = unknown)
  const s = screen || 0;
  const tier = /\b(pro|max|ultra)\b/i.test(chip || '');
  if (/pro/i.test(model)) {
    const P = `${A_CDN}/macbook-pro`;
    if (g === 1) return s >= 16 ? `${P}/macbook-pro-2021-16in.png` : s <= 13 ? `${P}/macbook-pro-2020-late-13in-device.jpg` : `${P}/macbook-pro-2021-14in.png`;
    if (g === 2) return s >= 16 ? `${P}/macbook-pro-16in-2023.png` : s <= 13 ? `${P}/macbook-pro-13-in-M2-2022.png` : `${P}/macbook-pro-14in-2023.png`;
    if (g === 3) return s >= 16 ? `${P}/macbook-pro-16in-m3-pro-m3-max-nov-2023-silver-space-black.png` : tier ? `${P}/macbook-pro-14in-m3-pro-m3-max-nov-2023-silver-space-black.png` : `${P}/macbook-pro-14in-m3-nov-2023-silver-space-gray.png`;
    return s >= 16 ? `${P}/macbook-pro-16in-2024-colors.png` : tier ? `${P}/macbook-pro-14in-2024-m4-pro-m4-max-colors.png` : `${P}/macbook-pro-14in-2024-m4-colors.png`;
  }
  if (/air/i.test(model) || /macbook/i.test(model)) {
    const A = `${A_CDN}/macbook-air`;
    if (g === 1) return `${A}/macbook-air-2020-late-device.jpg`;
    if (g === 2) return s >= 15 ? `${A}/2023-macbook-air-15in-m2-colors.png` : `${A}/2022-macbook-air-m2-colors.png`;
    if (g === 3) return s >= 15 ? `${A}/2024-macbook-air-15in-m3-colors.png` : `${A}/2024-macbook-air-13in-m3-colors.png`;
    return s >= 15 ? `${A}/2025-macbook-air-15in-colors.png` : `${A}/2025-macbook-air-13in-colors.png`;
  }
  return null;
}

async function api<T>(path: string): Promise<T> {
  const r = await fetch(path);
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json() as Promise<T>;
}
function money(s: string | number | null): string {
  if (s === null || s === '' || s === undefined) return '—';
  return '$' + Number(s).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

// ---- router ----------------------------------------------------------------
async function route(): Promise<void> {
  const segs = location.pathname.split('/').filter(Boolean); // ['shop', a?, b?]
  try {
    if (segs.length >= 3) return await renderProduct(decodeURIComponent(segs[2]!));
    return await renderGrid();
  } catch {
    app.innerHTML = `<div class="loading">Couldn't load that. <a href="/shop/macbook" data-spa>Back to MacBooks</a></div>`;
  }
}
function go(href: string): void { history.pushState(null, '', href); route(); }
addEventListener('popstate', route);
document.addEventListener('click', (e) => {
  const a = (e.target as HTMLElement).closest('a[data-spa]') as HTMLAnchorElement | null;
  if (a && a.href) { e.preventDefault(); go(new URL(a.href).pathname); }
});

// ---- derived truth: model #, cosmetic/screen flags from the listing title --
function modelNumber(title: string | null): string | null {
  if (!title) return null;
  const part = /\b([A-Z0-9]{5,7}\/A)\b/.exec(title);          // e.g. MGN63LL/A
  if (part) return part[1]!;
  const anum = /\b(A2\d{3}|A3\d{3})\b/.exec(title);           // e.g. A2337
  return anum ? anum[1]! : null;
}
interface Flag { key: string; label: string; sev: string; }
const COSMETIC_RULES: { re: RegExp; key: string; label: string; sev: string }[] = [
  { re: /\bcrack|cracked|shatter/i, key: 'screen', label: 'Cracked / damaged', sev: 'critical' },
  { re: /\b(for parts|parts only|not working|spares|as[\s-]?is|read (the )?desc|repair|faulty|defective|broken)\b/i, key: 'asis', label: 'As-is / for parts', sev: 'critical' },
  { re: /\b(dead pixel|dead pixels|image retention|burn[\s-]?in|screen burn|backlight bleed|lcd (issue|damage)|display (issue|damage)|line(s)? on screen)\b/i, key: 'screen', label: 'Screen issue', sev: 'major' },
  { re: /\b(dent|dents|ding|bent)\b/i, key: 'dent', label: 'Dents', sev: 'major' },
  { re: /\b(scratch|scratches|scuff|scuffs|scrapes?)\b/i, key: 'scratch', label: 'Scratches', sev: 'minor' },
  { re: /\b(wear|worn|blemish|marks?)\b/i, key: 'wear', label: 'Cosmetic wear', sev: 'minor' },
  { re: /\b(no charger|without charger|no adapter|missing|no box)\b/i, key: 'missing', label: 'Missing accessory', sev: 'minor' },
  { re: /\b(battery (service|replace)|battery health (8[0-4]|[0-7]\d)%|swollen battery)\b/i, key: 'battery', label: 'Battery worn', sev: 'major' },
];
function listingFlags(o: FlatOffer): Flag[] {
  const hay = `${o.title || ''} ${o.condition_grade || ''}`;
  const out: Flag[] = [];
  const seen = new Set<string>();
  for (const r of COSMETIC_RULES) {
    if (r.re.test(hay) && !seen.has(r.key)) { seen.add(r.key); out.push({ key: r.key, label: r.label, sev: r.sev }); }
  }
  return out;
}
// Sanity flags — catch listings that parsed a chip but clearly aren't a full
// laptop (no RAM AND no storage) or are priced implausibly low (parts, cases,
// chargers mislabelled with a MacBook title). Without these, junk shows a
// misleading green "No flags found".
function sanityFlags(o: FlatOffer): Flag[] {
  const out: Flag[] = [];
  const price = Number(o.price_usd);
  if (!o.ram_gb && !o.storage) out.push({ key: 'incomplete', label: "Specs incomplete — verify it's a full laptop", sev: 'major' });
  const newish = o.condition === 'new' || o.condition === 'refurb' || o.condition === 'open_box';
  if (price > 0 && ((newish && price < 400) || price < 150)) out.push({ key: 'cheap', label: 'Suspiciously cheap — likely parts / accessory', sev: 'critical' });
  return out;
}
// All fault signals (sanity + listing-derived + catalog faults) → unified chips + filter keys.
function allFlags(o: FlatOffer): Flag[] {
  const fromCatalog: Flag[] = o.faults.map(f => ({ key: f.category, label: FAULT_LABEL[f.category] || f.title, sev: f.severity }));
  const merged = [...sanityFlags(o), ...listingFlags(o), ...fromCatalog];
  const seen = new Set<string>(); const out: Flag[] = [];
  const rank: Record<string, number> = { critical: 0, major: 1, minor: 2, info: 3 };
  merged.sort((a, b) => (rank[a.sev] ?? 9) - (rank[b.sev] ?? 9));
  for (const f of merged) { if (!seen.has(f.key)) { seen.add(f.key); out.push(f); } }
  return out;
}

// ---- the grid --------------------------------------------------------------
interface GridState {
  q: string; sort: string;
  models: Set<string>; chips: Set<string>; mems: Set<number>; screens: Set<number>;
  conds: Set<string>; retailers: Set<string>; flags: Set<string>;
  maxPrice: number; inStockOnly: boolean;
}
const BATCH = 48;

function chipSortKey(chip: string): number {
  const m = /^M(\d)/i.exec(chip);
  const fam = m ? Number(m[1]) : 9;
  const tier = /max/i.test(chip) ? 2 : /pro/i.test(chip) ? 1 : 0;
  return fam * 10 + tier;
}

async function renderGrid(): Promise<void> {
  app.innerHTML = `<div class="loading">Loading every MacBook…</div>`;
  const { offers } = await api<{ offers: FlatOffer[] }>(`/api/shop/offers?category=laptops`);

  // Normalize chip once up front so facet, filters and cards all agree on the
  // canonical short label (M3, M4, M4 Pro) instead of verbose core-count strings.
  for (const o of offers) o.chip = normalizeChip(o.chip);

  // facets
  const models = Array.from(new Set(offers.map(o => o.model))).sort(); // MacBook Air, MacBook Pro
  const chips = Array.from(new Set(offers.map(o => o.chip).filter(Boolean) as string[])).sort((a, b) => chipSortKey(a) - chipSortKey(b));
  const mems = Array.from(new Set(offers.map(o => o.ram_gb).filter((n): n is number => !!n))).sort((a, b) => a - b);
  const screens = Array.from(new Set(offers.map(o => o.screen_in).filter((n): n is number => !!n))).sort((a, b) => a - b);
  const conds = Array.from(new Set(offers.map(o => o.condition))).sort((a, b) => COND_ORDER.indexOf(a) - COND_ORDER.indexOf(b));
  const retailers = Array.from(new Set(offers.map(o => o.retailer))).sort();
  const flagKeys = Array.from(new Set(offers.flatMap(o => allFlags(o).map(f => f.key))));
  const priceCeil = Math.ceil(Math.max(200, ...offers.map(o => Number(o.price_usd) || 0)) / 100) * 100;

  const state: GridState = {
    q: '', sort: 'price_asc', models: new Set(), chips: new Set(), mems: new Set(), screens: new Set(),
    conds: new Set(), retailers: new Set(), flags: new Set(), maxPrice: priceCeil, inStockOnly: false,
  };

  const checks = (items: (string | number)[], attr: string, label: (v: string | number) => string): string =>
    items.map(v => `<label class="f-check"><input type="checkbox" data-${attr}="${esc(String(v))}"><span>${esc(label(v))}</span></label>`).join('');

  app.innerHTML = `
    <header class="shop-hero">
      <h1>Every MacBook. Every seller. <span class="hl">One page.</span></h1>
      <p class="lede">${offers.length.toLocaleString()} listings across ${retailers.length} retailers — price, condition, seller rating and condition flags on every card. Tap to buy from the source.</p>
    </header>
    <div class="browse">
      <aside class="filters" id="filters">
        <div class="f-search"><input id="q" type="search" placeholder="Search title, chip, seller…" autocomplete="off"></div>

        <div class="f-group"><label class="f-label">Sort</label>
          <select id="sort" class="f-select">
            <option value="price_asc">Price: low → high</option>
            <option value="price_desc">Price: high → low</option>
            <option value="seller_desc">Seller rating: high → low</option>
            <option value="fewest_flags">Fewest red flags first</option>
          </select>
        </div>

        <div class="f-group"><label class="f-label">Max price <span id="priceVal" class="f-pill">${money(priceCeil)}</span></label>
          <input id="price" type="range" min="0" max="${priceCeil}" step="50" value="${priceCeil}" class="f-range"></div>

        <div class="f-group"><label class="f-label">Model</label><div class="f-checks">${checks(models, 'model', v => String(v).replace(/^MacBook /, ''))}</div></div>
        <div class="f-group"><label class="f-label">Chip</label><div class="f-checks">${checks(chips, 'chip', v => String(v))}</div></div>
        <div class="f-group"><label class="f-label">Memory</label><div class="f-checks f-wrap">${checks(mems, 'mem', v => `${v}GB`)}</div></div>
        <div class="f-group"><label class="f-label">Screen</label><div class="f-checks f-wrap">${checks(screens, 'screen', v => `${v}″`)}</div></div>
        <div class="f-group"><label class="f-label">Condition</label><div class="f-checks">${checks(conds, 'cond', v => COND_LABEL[String(v)] || String(v))}</div></div>
        <div class="f-group"><label class="f-label">Retailer</label><div class="f-checks">${checks(retailers, 'retailer', v => RETAILER_LABEL[String(v)] || String(v))}</div></div>
        <div class="f-group"><label class="f-label">Hide listings with</label><div class="f-checks">${checks(flagKeys, 'flag', v => FAULT_LABEL[String(v)] || flagLabelOf(String(v)))}</div>
          <p class="f-hint">Checked = exclude any listing flagged with that issue.</p></div>

        <label class="f-check f-toggle"><input type="checkbox" id="instock"><span>In-stock only</span></label>
        <button class="f-clear" id="clear">Reset all filters</button>
      </aside>

      <section class="browse-main">
        <div class="browse-bar"><span id="count" class="count"></span><span class="browse-note">Condition &amp; flags on every listing · outbound links may be affiliate</span></div>
        <div id="grid" class="offer-grid"></div>
        <div id="sentinel" class="sentinel"></div>
      </section>
    </div>`;

  const grid = document.getElementById('grid')!;
  const count = document.getElementById('count')!;
  let filtered: FlatOffer[] = [];
  let rendered = 0;

  const computeFlagKeys = new Map<string, Set<string>>();
  const flagsOf = (o: FlatOffer): Set<string> => {
    let s = computeFlagKeys.get(o.id);
    if (!s) { s = new Set(allFlags(o).map(f => f.key)); computeFlagKeys.set(o.id, s); }
    return s;
  };

  const renderMore = (): void => {
    const next = filtered.slice(rendered, rendered + BATCH);
    if (!next.length) return;
    grid.insertAdjacentHTML('beforeend', next.map(offerCard).join(''));
    rendered += next.length;
  };

  // Multi-select facet groups, each with its data accessor. Used for both
  // filtering and cascading availability (disable options with no matches).
  const FACETS: { attr: string; sel: () => Set<unknown>; get: (o: FlatOffer) => string | number | null }[] = [
    { attr: 'model', sel: () => state.models as Set<unknown>, get: o => o.model },
    { attr: 'chip', sel: () => state.chips as Set<unknown>, get: o => o.chip },
    { attr: 'mem', sel: () => state.mems as Set<unknown>, get: o => o.ram_gb },
    { attr: 'screen', sel: () => state.screens as Set<unknown>, get: o => o.screen_in },
    { attr: 'cond', sel: () => state.conds as Set<unknown>, get: o => o.condition },
    { attr: 'retailer', sel: () => state.retailers as Set<unknown>, get: o => o.retailer },
  ];
  const facetPass = (o: FlatOffer, f: typeof FACETS[number]): boolean => {
    const s = f.sel(); if (!s.size) return true; const v = f.get(o); return v != null && s.has(v);
  };
  // Non-facet constraints (price, in-stock, fault-exclusion, search).
  const otherPass = (o: FlatOffer): boolean => {
    if (Number(o.price_usd) > state.maxPrice) return false;
    if (state.inStockOnly && !o.in_stock) return false;
    if (state.flags.size) { const fk = flagsOf(o); for (const k of state.flags) if (fk.has(k)) return false; }
    if (state.q) { const q = state.q.toLowerCase(); if (!(`${o.title || o.name} ${o.chip || ''} ${o.seller || ''} ${o.retailer}`.toLowerCase().includes(q))) return false; }
    return true;
  };

  // Grey out any option that, combined with the OTHER active filters, would
  // return zero listings — so each tap narrows the still-valid choices. Already
  // selected options stay enabled so they can be un-ticked.
  const updateAvailability = (): void => {
    for (const f of FACETS) {
      const avail = new Set<string>();
      for (const o of offers) {
        if (!otherPass(o)) continue;
        if (!FACETS.every(g => g === f || facetPass(o, g))) continue;
        const v = f.get(o); if (v != null) avail.add(String(v));
      }
      app.querySelectorAll<HTMLInputElement>(`[data-${f.attr}]`).forEach(cb => {
        const enabled = avail.has(cb.dataset[f.attr]!) || cb.checked;
        cb.disabled = !enabled;
        cb.closest('.f-check')?.classList.toggle('f-disabled', !enabled);
      });
    }
  };

  const apply = (): void => {
    filtered = offers.filter(o => otherPass(o) && FACETS.every(f => facetPass(o, f)));
    sortOffers(filtered, state.sort);
    count.textContent = `${filtered.length.toLocaleString()} of ${offers.length.toLocaleString()} listings`;
    updateAvailability();
    grid.innerHTML = '';
    rendered = 0;
    if (!filtered.length) { grid.innerHTML = `<div class="loading">No MacBooks match these filters. <button class="linklike" id="reset">Reset</button></div>`; document.getElementById('reset')?.addEventListener('click', () => (document.getElementById('clear') as HTMLButtonElement).click()); return; }
    renderMore();
  };

  // incremental render on scroll
  const sentinel = document.getElementById('sentinel')!;
  new IntersectionObserver((es) => { if (es[0]!.isIntersecting) renderMore(); }, { rootMargin: '800px' }).observe(sentinel);

  // wire controls
  const onCheck = (attr: string, set: Set<string> | Set<number>, numeric = false): void => {
    app.querySelectorAll<HTMLInputElement>(`[data-${attr}]`).forEach(cb => cb.addEventListener('change', () => {
      const v = numeric ? Number(cb.dataset[attr]) : cb.dataset[attr]!;
      cb.checked ? (set as Set<unknown>).add(v) : (set as Set<unknown>).delete(v);
      apply();
    }));
  };
  (document.getElementById('q') as HTMLInputElement).addEventListener('input', e => { state.q = (e.target as HTMLInputElement).value; apply(); });
  (document.getElementById('sort') as HTMLSelectElement).addEventListener('change', e => { state.sort = (e.target as HTMLSelectElement).value; apply(); });
  const priceEl = document.getElementById('price') as HTMLInputElement;
  priceEl.addEventListener('input', e => { state.maxPrice = Number((e.target as HTMLInputElement).value); document.getElementById('priceVal')!.textContent = money(state.maxPrice); apply(); });
  (document.getElementById('instock') as HTMLInputElement).addEventListener('change', e => { state.inStockOnly = (e.target as HTMLInputElement).checked; apply(); });
  onCheck('model', state.models); onCheck('chip', state.chips); onCheck('mem', state.mems, true); onCheck('screen', state.screens, true);
  onCheck('cond', state.conds); onCheck('retailer', state.retailers); onCheck('flag', state.flags);
  document.getElementById('clear')!.addEventListener('click', () => {
    state.q = ''; state.sort = 'price_asc'; state.maxPrice = priceCeil; state.inStockOnly = false;
    [state.models, state.chips, state.mems, state.screens, state.conds, state.retailers, state.flags].forEach(s => (s as Set<unknown>).clear());
    (document.getElementById('q') as HTMLInputElement).value = '';
    (document.getElementById('sort') as HTMLSelectElement).value = 'price_asc';
    (document.getElementById('instock') as HTMLInputElement).checked = false;
    priceEl.value = String(priceCeil); document.getElementById('priceVal')!.textContent = money(priceCeil);
    app.querySelectorAll<HTMLInputElement>('.filters input[type=checkbox]').forEach(cb => (cb.checked = false));
    apply();
  });

  apply();
}

function flagLabelOf(key: string): string {
  const map: Record<string, string> = { screen: 'Screen issue', dent: 'Dents', scratch: 'Scratches', wear: 'Cosmetic wear', asis: 'As-is / parts', missing: 'Missing accessory', incomplete: 'Incomplete specs', cheap: 'Suspiciously cheap', battery: 'Battery worn' };
  return map[key] || key;
}

function sortOffers(list: FlatOffer[], sort: string): void {
  if (sort === 'price_desc') list.sort((a, b) => Number(b.price_usd) - Number(a.price_usd));
  else if (sort === 'seller_desc') list.sort((a, b) => sellerPct(b) - sellerPct(a) || Number(a.price_usd) - Number(b.price_usd));
  else if (sort === 'fewest_flags') list.sort((a, b) => allFlags(a).length - allFlags(b).length || Number(a.price_usd) - Number(b.price_usd));
  else list.sort((a, b) => Number(a.price_usd) - Number(b.price_usd)); // price_asc
}
function sellerPct(o: FlatOffer): number { const m = /([\d.]+)\s*%/.exec(o.seller_rating || ''); return m ? Number(m[1]) : -1; }

// Root-level risk rule shared by every card. A listing gets the loud warning
// treatment when it carries any critical flag (parts / defective / cracked /
// as-is) OR is priced at or under $500 — at that price a MacBook is almost
// always old, parts-only, or not as described, even when the title looks clean.
const CHEAP_CEILING = 500;
function cardRisk(o: FlatOffer, flags: Flag[]): { level: 'high' | 'caution'; label: string } | null {
  if (flags.some(f => f.sev === 'critical'))
    return { level: 'high', label: 'High risk — likely parts-only, defective, or not as described. Read the full listing before paying.' };
  const price = Number(o.price_usd);
  if (price > 0 && price <= CHEAP_CEILING)
    return { level: 'caution', label: `Under $${CHEAP_CEILING} — unusually cheap for a MacBook. Confirm it powers on and check battery, warranty & authenticity before buying.` };
  return null;
}

// ---- one listing card ------------------------------------------------------
function offerCard(o: FlatOffer): string {
  const buyUrl = o.affiliate_url || o.url;
  const retailer = RETAILER_LABEL[o.retailer] || o.retailer;
  const flags = allFlags(o);
  const risk = cardRisk(o, flags);
  const list = o.list_price_usd ? Number(o.list_price_usd) : null;
  const pct = list && list > Number(o.price_usd) ? Math.round((1 - Number(o.price_usd) / list) * 100) : null;
  const macos = macosUntil(o.chip);
  const mnum = modelNumber(o.title);
  const sellerScore = sellerPct(o);
  const screen = o.screen_in ? `${o.screen_in}″` : '';
  const ram = o.ram_gb ? `${o.ram_gb}GB` : '';
  const title = `${o.brand} ${o.model}${screen ? ` ${screen}` : ''}${o.chip ? ` · ${o.chip}` : ''}`;
  const img = macbookImage(o.model, o.chip, o.screen_in) || o.image_url;

  return `
  <a class="ocard${risk ? ` ocard-${risk.level}` : ''}" href="${esc(buyUrl)}" target="_blank" rel="nofollow noopener sponsored">
    ${risk ? `<div class="oc-warn warn-${risk.level}"><span class="oc-warn-i">⚠</span><span>${esc(risk.label)}</span></div>` : ''}
    <div class="oc-img">
      ${img ? `<img src="${esc(img)}" alt="${esc(o.model)}" loading="lazy" onerror="this.closest('.oc-img').classList.add('noimg');this.remove()">` : ''}
      <span class="oc-ph">${esc(o.model || 'MacBook')}</span>
      <span class="oc-cond cond-${esc(o.condition)}">${esc(COND_LABEL[o.condition] || o.condition)}</span>
    </div>
    <div class="oc-body">
      <div class="oc-title">${esc(title)}</div>
      <div class="oc-specs">
        ${o.chip ? `<span class="spec">${esc(o.chip)}</span>` : ''}
        ${ram ? `<span class="spec">${esc(ram)}</span>` : ''}
        ${o.storage ? `<span class="spec">${esc(o.storage)}</span>` : ''}
        ${screen ? `<span class="spec">${esc(screen)}</span>` : ''}
      </div>
      <div class="oc-sub">
        ${mnum ? `<span class="oc-model" title="Apple model number">#${esc(mnum)}</span>` : ''}
        ${macos ? `<span class="oc-macos" title="Estimated — Apple Silicon Macs get ~7 macOS releases from launch">macOS est. ~${esc(macos)}</span>` : ''}
      </div>
      ${flags.length ? `<div class="oc-flags">${flags.slice(0, 4).map(f => `<span class="flag sev-${esc(f.sev)}" title="${esc(f.label)}">${esc(f.label)}</span>`).join('')}${flags.length > 4 ? `<span class="flag more">+${flags.length - 4}</span>` : ''}</div>` : `<div class="oc-flags"><span class="flag clean">No flags found</span></div>`}
      <div class="oc-seller">
        <span class="oc-ret">${esc(retailer)}</span>
        ${o.seller && o.seller.toLowerCase() !== o.retailer ? `<span class="oc-name">· ${esc(o.seller)}</span>` : ''}
        ${sellerScore >= 0 ? `<span class="oc-rating ${sellerScore >= 98 ? 'good' : sellerScore >= 95 ? 'ok' : 'low'}" title="Seller rating on ${esc(retailer)}">★ ${esc(o.seller_rating!)}</span>` : ''}
      </div>
      <div class="oc-foot">
        <div class="oc-price"><b>${money(o.price_usd)}</b>${pct ? `<span class="oc-off">${pct}% off list</span>` : ''}${!o.in_stock ? `<span class="oc-oos">out of stock</span>` : ''}</div>
        <span class="oc-buy">Buy at ${esc(retailer)} →</span>
      </div>
    </div>
  </a>`;
}

// ---- single product (deep links) — de-graded, faults up front --------------
async function renderProduct(slug: string): Promise<void> {
  const p = await api<ProductView>(`/api/shop/product/${encodeURIComponent(slug)}`);
  const msrp = p.msrp_usd ? Number(p.msrp_usd) : null;
  const chip = normalizeChip((p.specs?.chip as string) || null);
  const macos = macosUntil(chip);
  const img = macbookImage(p.model, chip, (p.specs?.screen_in as number) || null) || p.image_url;
  app.innerHTML = `
    <a class="back" href="/shop/macbook" data-spa>← all MacBooks</a>
    <header class="truth-hero">
      <div class="th-media">${img ? `<img src="${esc(img)}" alt="${esc(p.name)}" onerror="this.parentElement.classList.add('noimg')">` : ''}</div>
      <div class="th-info">
        <div class="th-brand">${esc(p.brand)}</div>
        <h1>${esc(p.model)} <span class="th-variant">${esc(p.variant || '')}</span></h1>
        <div class="th-specrow">
          ${chip ? `<span class="spec">${esc(chip)}</span>` : ''}
          ${p.specs?.ram_gb ? `<span class="spec">${esc(String(p.specs.ram_gb))}GB</span>` : ''}
          ${p.specs?.storage ? `<span class="spec">${esc(String(p.specs.storage))}</span>` : ''}
          ${p.specs?.screen_in ? `<span class="spec">${esc(String(p.specs.screen_in))}″</span>` : ''}
          ${macos ? `<span class="spec macos">macOS est. ~${esc(macos)}</span>` : ''}
        </div>
        ${p.summary ? `<div class="th-summary"><span class="th-summary-tag">Before you buy</span><p>${esc(p.summary)}</p></div>` : ''}
        <div class="th-anchors">
          ${msrp ? `<div class="anchor"><span>MSRP</span><b>${money(p.msrp_usd)}</b></div>` : ''}
          <div class="anchor"><span>Lowest price</span><b>${money(p.best_price)}</b></div>
        </div>
      </div>
    </header>
    <section class="offers">
      <div class="offers-head"><h2>${p.offers.length} listings</h2><span class="offers-sub">Cheapest first.</span></div>
      ${[...p.offers].sort((a, b) => Number(a.price_usd) - Number(b.price_usd)).map(o => productOfferRow(o, msrp)).join('')}
    </section>`;
}

function productOfferRow(o: OfferView, msrp: number | null): string {
  const vsMsrp = msrp && o.price_usd ? Math.round((1 - Number(o.price_usd) / msrp) * 100) : null;
  const flags = allFlags(o);
  const buyUrl = o.affiliate_url || o.url;
  const retailer = RETAILER_LABEL[o.retailer] || o.retailer;
  return `
  <article class="offer${flags.some(f => f.sev === 'critical') ? ' offer-risk' : ''}">
    <div class="of-main">
      <div class="of-id">
        <div class="of-retailer">${esc(retailer)}
          <span class="cond cond-${esc(o.condition)}">${esc(COND_LABEL[o.condition] || o.condition)}${o.condition_grade ? ` · ${esc(o.condition_grade)}` : ''}</span>
        </div>
        <div class="of-meta">${o.seller ? `<span>${esc(o.seller)}</span>` : ''}${o.seller_rating ? `<span>· ★ ${esc(o.seller_rating)}</span>` : ''}<span class="prov prov-${esc(o.crawl_source)}">${o.crawl_source === 'api' ? 'verified via API' : 'crawled'}</span></div>
      </div>
      <div class="of-price"><b>${money(o.price_usd)}</b>${vsMsrp && vsMsrp > 0 ? `<span class="off">${vsMsrp}% off MSRP</span>` : ''}${!o.in_stock ? `<span class="oos">out of stock</span>` : ''}</div>
      <a class="of-buy" href="${esc(buyUrl)}" target="_blank" rel="nofollow noopener sponsored">Buy at ${esc(retailer)} →</a>
    </div>
    ${flags.length ? `<div class="of-faults"><div class="ff-head">🚩 Know before you buy</div><ul>${o.faults.map(f => `<li class="fault sev-${esc(f.severity)}"><span class="fdot"></span><div><b>${esc(f.title)}</b>${f.verified ? ' <span class="verified">✓ verified</span>' : ' <span class="unverified">unverified</span>'}<p>${esc(f.detail)}</p></div></li>`).join('')}</ul></div>` : ''}
    ${o.claims.length ? `<div class="of-claims">${o.claims.map(c => `<div class="claim delta-${esc(c.delta)}"><div class="cl-row"><span class="cl-said">They say: “${esc(c.claim_text)}”</span><span class="cl-tag">${esc(DELTA_LABEL[c.delta] || c.delta)}</span></div>${c.reality ? `<div class="cl-real"><b>Reality:</b> ${esc(c.reality)}</div>` : ''}</div>`).join('')}</div>` : ''}
  </article>`;
}

route();
